import { del, get, patch, post, put } from '../utils/networking';

export type HmsVersionStatus = 'DRAFT' | 'PENDING' | 'RETURNED' | 'APPROVED' | 'SUPERSEDED';
export type HmsApprovalMode = 'SAFETY_REP' | 'HR_WITHOUT_SAFETY_REP';

export type HmsApproval = {
  mode: HmsApprovalMode;
  approvedAt: string;
  comment: string | null;
  approvedByEmail: string | null;
  recordedByEmail: string | null;
};

/** The safety representative sent the version back with what is missing */
export type HmsReturned = {
  comment: string;
  returnedAt: string;
  returnedByEmail: string | null;
};

export type HmsApprovalInput = { mode: HmsApprovalMode; comment?: string };

export type RiskMatrixSettings = {
  moderateFrom: number;
  highFrom: number;
  likelihoodBands: number[];
  deadlineHighDays: number;
  deadlineModerateDays: number;
  surveyIntervalMonths: number;
  minResponses: number;
  combinedAssessmentFrom: number;
  oneReportHighFactorKeys: string[];
  consequenceOverrides: Record<string, number>;
  whistleblowingUrl: string | null;
  followUpRoutineUrl: string | null;
  /** Employees may skip the sensitive parts and report through the whistleblowing channel instead */
  allowSensitiveSkip: boolean;
};

export type RiskMatrixChange =
  | {
      type:
        | 'moderateFrom'
        | 'highFrom'
        | 'deadlineHighDays'
        | 'deadlineModerateDays'
        | 'surveyIntervalMonths'
        | 'minResponses'
        | 'combinedAssessmentFrom';
      from: number;
      to: number;
    }
  | { type: 'likelihoodBands'; from: number[]; to: number[] }
  | { type: 'consequence'; factorKey: string; from: number; to: number }
  | { type: 'oneReport'; factorKey: string; to: boolean }
  | { type: 'whistleblowingUrl' | 'followUpRoutineUrl' }
  | { type: 'allowSensitiveSkip'; to: boolean };

export type RiskMatrixVersion = {
  id: number;
  version: number;
  status: HmsVersionStatus;
  settings: RiskMatrixSettings;
  changeReason: string | null;
  changes: RiskMatrixChange[];
  submittedAt: string | null;
  createdAt: string;
  approval: HmsApproval | null;
  returned: HmsReturned | null;
};

export type RiskMatrixFactor = {
  key: string;
  name: string;
  source: 'STANDARD' | 'CUSTOM';
  consequence: number;
  standardConsequence: number;
};

/** The values each setting can take, as the API accepts them */
export type RiskMatrixOptions = {
  moderateFrom: number[];
  highFrom: number[];
  deadlineHighDays: number[];
  deadlineModerateDays: number[];
  surveyIntervalMonths: number[];
  minResponses: number[];
  combinedAssessmentFrom: number[];
};

export type RiskMatrixOverview = {
  current: RiskMatrixVersion;
  pending: RiskMatrixVersion | null;
  returned: RiskMatrixVersion | null;
  history: RiskMatrixVersion[];
  factors: RiskMatrixFactor[];
  options: RiskMatrixOptions;
  hasMainSafetyRep: boolean;
  /** The HR user is a main safety representative, and cannot approve their own changes in that role */
  mainSafetyRepIsYou: boolean;
  /** The HR user made the version that waits, as a main safety representative too, so another one must approve it */
  pendingIsYours?: boolean;
  teamCount: number;
  organizationName: string;
};

export type SubmitRiskMatrixRequest = {
  settings: RiskMatrixSettings;
  changeReason: string;
  approval: HmsApprovalInput;
};

/** AI suggestions are not shown to employees until HR has approved them */
export type Translation = { text: string; aiDraft: boolean };

export type Translations = Record<string, Translation>;

export type TemplateQuestion = {
  key: string;
  text: string;
  help: string | null;
  highIsGood: boolean;
  /** How often something has happened the last 12 months. Once is enough to count. */
  isIncident?: boolean;
  translations: Translations;
};

/** A strain on the work environment, or a resource that protects against it */
export type FactorDimension = 'LOAD' | 'RESOURCE';

export type TemplateFactor = {
  key: string;
  name: string;
  description: string;
  consequence: number;
  dimension: FactorDimension;
  source: 'STANDARD' | 'CUSTOM';
  relevanceReason: string | null;
  isSensitive: boolean;
  nameTranslations: Translations;
  descriptionTranslations: Translations;
  questions: TemplateQuestion[];
};

export type TranslationKind =
  | 'factorName'
  | 'factorDescription'
  | 'question'
  | 'riskLevel'
  | 'consequenceName'
  | 'consequenceDescription'
  | 'answerScale'
  | 'incidentScale';

/** One text that can be translated, with what exists in the language */
export type TranslationItem = {
  key: string;
  kind: TranslationKind;
  factorKey: string | null;
  factorName: string | null;
  /** Question number or level */
  position: number | null;
  source: string;
  /** Virksomhetens egen oversettelse, eller tom */
  text: string;
  aiDraft: boolean;
  /** The Trustpulse translation, used when the organisation has not written its own */
  standard: string | null;
  translated: boolean;
};

/** A language in addition to bokmål. The name is only set for languages not in the list. */
export type TemplateLanguage = { code: string; name: string | null };

export type TemplateLanguageView = TemplateLanguage & {
  direction: 'ltr' | 'rtl';
  translated: number;
  total: number;
  items: TranslationItem[];
};

export type TranslationEntry = { key: string; text: string; aiDraft: boolean };

/** New factors and questions have no key yet */
export type TemplateFactorInput = {
  key?: string;
  name: string;
  description?: string;
  consequence: number;
  dimension: FactorDimension;
  relevanceReason?: string | null;
  questions: Array<{ key?: string; text: string; highIsGood: boolean }>;
};

export type TemplateChange =
  | { type: 'factorRemoved'; factorKey: string; name: string }
  | { type: 'factorAdded'; factorKey: string; name: string; questions: number }
  | {
      type: 'questionsAdded' | 'questionsEdited' | 'questionsRemoved';
      factorKey: string;
      name: string;
      count: number;
    }
  | { type: 'dimensionChanged'; factorKey: string; name: string; to: FactorDimension }
  | { type: 'languagesChanged'; languages: string[] }
  | { type: 'translationsChanged'; language: string };

export type TemplateWarnings = {
  fewQuestions: string[];
  legallyRequiredRemoved: string[];
};

export type TemplateVersionSummary = {
  id: number;
  version: number;
  status: HmsVersionStatus;
  availableForNew: boolean;
  recommended: boolean;
  factorCount: number;
  questionCount: number;
  submittedAt: string | null;
  createdAt: string;
  approval: HmsApproval | null;
  returned: HmsReturned | null;
};

export type TemplateVersionContent = TemplateVersionSummary & {
  factors: TemplateFactor[];
  warnings: TemplateWarnings;
  languages: TemplateLanguageView[];
};

export type TemplateOverview = {
  organizationName: string;
  /** Languages HR can choose from the list */
  selectableLanguages: string[];
  hasMainSafetyRep: boolean;
  /** The HR user is a main safety representative, and cannot approve their own changes in that role */
  mainSafetyRepIsYou: boolean;
  /** The HR user made the version that waits, as a main safety representative too, so another one must approve it */
  pendingIsYours?: boolean;
  versions: TemplateVersionSummary[];
  current: TemplateVersionContent;
  draft: (TemplateVersionContent & { changes: TemplateChange[] }) | null;
};

export type TemplateVersionDetail = TemplateVersionContent & {
  previousVersion: number | null;
  changes: TemplateChange[];
};

export const getHmsRiskMatrix = () => get<RiskMatrixOverview>('/api/hms/risk-matrix');

/** Whether HR has changed the risk matrix from the standard, and the routine links the employees will not get */
export type RiskMatrixSetup = {
  changed: boolean;
  missingRoutines: Array<'whistleblowing' | 'followUp'>;
};

/** For anyone who creates a work environment survey, not only HR */
export const getRiskMatrixSetup = () => get<RiskMatrixSetup>('/api/work-environment/risk-matrix/setup');

export const submitHmsRiskMatrix = (request: SubmitRiskMatrixRequest) =>
  post<RiskMatrixOverview>('/api/hms/risk-matrix/versions', request);

export const approveHmsRiskMatrix = (id: number, comment: string) =>
  post<RiskMatrixOverview>(`/api/hms/risk-matrix/versions/${id}/approve`, {
    comment,
  });

export const withdrawHmsRiskMatrix = (id: number) => del<RiskMatrixOverview>(`/api/hms/risk-matrix/versions/${id}`);

export const getHmsTemplate = () => get<TemplateOverview>('/api/hms/template');

export const getHmsTemplateVersion = (id: number) => get<TemplateVersionDetail>(`/api/hms/template/versions/${id}`);

export const saveHmsTemplateDraft = (factors: TemplateFactorInput[]) =>
  put<TemplateOverview>('/api/hms/template/draft', { factors });

export const saveHmsTemplateLanguages = (languages: TemplateLanguage[]) =>
  put<TemplateOverview>('/api/hms/template/draft/languages', { languages });

export const saveHmsTranslations = (language: string, entries: TranslationEntry[]) =>
  put<TemplateOverview>(`/api/hms/template/draft/translations/${encodeURIComponent(language)}`, { entries });

export const suggestHmsTranslations = (language: string) =>
  post<TemplateOverview>(`/api/hms/template/draft/translations/${encodeURIComponent(language)}/suggestions`);

export const discardHmsTemplateDraft = () => del<TemplateOverview>('/api/hms/template/draft');

export const submitHmsTemplateDraft = (approval: HmsApprovalInput) =>
  post<TemplateOverview>('/api/hms/template/draft/submit', approval);

export const approveHmsTemplateVersion = (id: number, comment: string) =>
  post<TemplateOverview>(`/api/hms/template/versions/${id}/approve`, {
    comment,
  });

export const withdrawHmsTemplateVersion = (id: number) =>
  post<TemplateOverview>(`/api/hms/template/versions/${id}/withdraw`);

export const setHmsTemplateAvailability = (id: number, availableForNew: boolean) =>
  patch<TemplateOverview>(`/api/hms/template/versions/${id}/availability`, {
    availableForNew,
  });

export type HmsSubject = 'riskMatrix' | 'template';

export type SafetyRepPending =
  | ({ subject: 'riskMatrix' } & RiskMatrixVersion)
  | ({ subject: 'template' } & TemplateVersionContent & {
        changes: TemplateChange[];
      });

export type SafetyRepSigned =
  | ({ subject: 'riskMatrix' } & RiskMatrixVersion)
  | ({ subject: 'template' } & TemplateVersionSummary);

export type SafetyRepOverview = {
  organizationName: string;
  isMain: boolean;
  pending: SafetyRepPending[];
  history: SafetyRepSigned[];
};

export type SafetyRepRiskMatrixVersion = {
  version: RiskMatrixVersion;
  current: RiskMatrixVersion;
  factors: RiskMatrixFactor[];
  organizationName: string;
};

const SUBJECT_PATHS: Record<HmsSubject, string> = {
  riskMatrix: 'risk-matrix',
  template: 'template',
};

export const getHmsSafetyRepOverview = () => get<SafetyRepOverview>('/api/hms/safety-rep');

export const getHmsSafetyRepRiskMatrixVersion = (id: number) =>
  get<SafetyRepRiskMatrixVersion>(`/api/hms/safety-rep/risk-matrix/versions/${id}`);

export const getHmsSafetyRepTemplateVersion = (id: number) =>
  get<TemplateVersionDetail>(`/api/hms/safety-rep/template/versions/${id}`);

export const approveHmsAsSafetyRep = (subject: HmsSubject, id: number) =>
  post<SafetyRepOverview>(`/api/hms/safety-rep/${SUBJECT_PATHS[subject]}/versions/${id}/approve`, { confirmed: true });

export const returnHmsAsSafetyRep = (subject: HmsSubject, id: number, comment: string) =>
  post<SafetyRepOverview>(`/api/hms/safety-rep/${SUBJECT_PATHS[subject]}/versions/${id}/return`, { comment });
